import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useSchool } from '../context/SchoolContext';
import { useLiveMode } from '../context/LiveModeContext';
import { AttentionGetterOverlay } from '../components/AttentionGetterOverlay';
import { StudentAppreciationOverlay } from '../components/StudentAppreciationOverlay';
import {
  Users, CheckCircle, AlertCircle, Clock, FileText,
  Shuffle, PenLine, Timer, Grid3X3, Calculator, Dices,
  Volume2, Radio, Play, Square, UserCheck, UserX,
  ThumbsUp, ThumbsDown, DoorOpen, Megaphone, Star,
  CalendarClock, Sun, Plus
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE } from '../apiConfig';

const API = API_BASE;

const MANAGEMENT_TOOLS = [
  { path: 'attendance', label: 'Attendance', desc: 'Mark the roll', icon: CheckCircle },
  { path: 'behaviour', label: 'Behaviour', desc: 'Log positive & negative', icon: AlertCircle },
  { path: 'out-of-class', label: 'Out of Class', desc: "Track who's out", icon: Clock },
  { path: 'lessons', label: 'Classwork', desc: 'Track activity progress', icon: FileText },
  { path: 'students', label: 'Students', desc: 'View class list', icon: Users },
];

const TEACHER_TOOLS = [
  { path: 'attention', label: 'Attention Getter', desc: 'Countdown and focus', icon: Megaphone, overlay: true },
  { path: 'appreciation', label: 'Star Students', desc: 'Celebrate top students', icon: Star, overlay: true },
  { path: 'teams', label: 'Team Maker', desc: 'Random groups', icon: Shuffle },
  { path: 'whiteboard', label: 'Whiteboard', desc: 'Draw and scribble', icon: PenLine },
  { path: 'student-picker', label: 'Random Student', desc: 'Select random students', icon: Dices },
  { path: 'seating-plan', label: 'Seating Plan', desc: 'Arrange student seats', icon: Grid3X3 },
  { path: 'timer', label: 'Timer', desc: 'Countdown timer', icon: Timer, global: true },
  { path: 'calculator', label: 'Calculator', desc: 'Scientific calculator', icon: Calculator, global: true },
  { path: 'noise-meter', label: 'Noise Meter', desc: 'Monitor volume', icon: Volume2, global: true },
];

const ToolCard = ({ tool, classId, index, accent, onOverlayClick }) => {
  const Icon = tool.icon;

  if (tool.overlay) {
    return (
      <div onClick={() => onOverlayClick(tool.path)} className="block cursor-pointer">
        <Card
          className={`border ${accent.border} ${accent.bg} hover:shadow-md transition-all cursor-pointer rounded-xl animate-fade-in`}
          style={{ animationDelay: `${index * 30}ms` }}
          data-testid={`tool-${tool.path}`}
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className={`w-11 h-11 rounded-xl ${accent.icon} flex items-center justify-center flex-shrink-0 shadow-sm`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className={`font-semibold text-sm ${accent.text}`}>{tool.label}</p>
              <p className="text-xs text-slate-400 mt-0.5">{tool.desc}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const linkPath = tool.global ? `/app/${tool.path}` : `/app/${tool.path}/${classId}`;
  return (
    <Link to={linkPath} className="block">
      <Card
        className={`border ${accent.border} ${accent.bg} hover:shadow-md transition-all cursor-pointer rounded-xl animate-fade-in`}
        style={{ animationDelay: `${index * 30}ms` }}
        data-testid={`tool-${tool.path}`}
      >
        <CardContent className="p-5 flex items-center gap-4">
          <div className={`w-11 h-11 rounded-xl ${accent.icon} flex items-center justify-center flex-shrink-0 shadow-sm`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div className="min-w-0">
            <p className={`font-semibold text-sm ${accent.text}`}>{tool.label}</p>
            <p className="text-xs text-slate-400 mt-0.5">{tool.desc}</p>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const MGMT_ACCENT = { bg: 'bg-blue-50/60', border: 'border-blue-100', icon: 'bg-blue-600', text: 'text-blue-800' };
const TOOL_ACCENT = { bg: 'bg-amber-50/60', border: 'border-amber-100', icon: 'bg-amber-600', text: 'text-amber-800' };

const ElapsedTimer = ({ startedAt }) => {
  const [elapsed, setElapsed] = useState('00:00');
  useEffect(() => {
    const start = new Date(startedAt).getTime();
    const tick = () => {
      const diff = Math.floor((Date.now() - start) / 1000);
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      setElapsed(
        h > 0
          ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
          : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [startedAt]);
  return <span data-testid="elapsed-timer">{elapsed}</span>;
};

export const LiveModePage = () => {
  const navigate = useNavigate();
  const { selectedSchool } = useSchool();
  const { session, stats, startSession, endSession, refreshStats } = useLiveMode();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [starting, setStarting] = useState(false);
  const [ending, setEnding] = useState(false);
  const [activeOverlay, setActiveOverlay] = useState(null);
  const [wholeDay, setWholeDay] = useState(false);
  const [specialDay, setSpecialDay] = useState(false);
  const [specialBellOpen, setSpecialBellOpen] = useState(false);
  const [customBells, setCustomBells] = useState([]);
  const [appreciationData, setAppreciationData] = useState({ logs: [], students: [] });
  const [createClassOpen, setCreateClassOpen] = useState(false);
  const [newClass, setNewClass] = useState({ name: '', class_code: '' });

  const periodsCount = selectedSchool?.periods_per_day || 6;

  const fetchClasses = async () => {
      try {
        const r = await fetch(`${API}/classes?school_id=${selectedSchool.school_id}`, { credentials: 'include' });
        if (r.ok) {
          const data = await r.json();
          setClasses(data.filter(c => !c.archived));
        }
      } catch (e) { console.error('Failed to fetch classes:', e); }
      finally { setLoading(false); }
    };

  useEffect(() => {
    if (!selectedSchool) return;
    fetchClasses();
  }, [selectedSchool]);

  const handleCreateClass = async () => {
    if (!newClass.name.trim()) { toast.error('Class name is required'); return; }
    try {
      const r = await fetch(`${API}/classes`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ ...newClass, school_id: selectedSchool.school_id })
      });
      if (r.ok) {
        const created = await r.json();
        toast.success('Class created');
        setCreateClassOpen(false);
        setNewClass({ name: '', class_code: '' });
        await fetchClasses();
        setSelectedClassId(created.class_id);
      } else toast.error('Failed to create class');
    } catch (e) { toast.error('Failed to create class'); }
  };

  const activeClass = useMemo(() => {
    if (session?.class_id) return classes.find(c => c.class_id === session.class_id);
    return null;
  }, [session, classes]);

  const handleStart = async () => {
    if (!selectedClassId) { toast.error('Select a class first'); return; }
    if (!wholeDay && !selectedPeriod) { toast.error('Select a period or use Whole Day'); return; }
    setStarting(true);
    const period = wholeDay ? 0 : parseInt(selectedPeriod);
    const result = await startSession(selectedClassId, period);
    setStarting(false);
    if (result) {
      const cls = classes.find(c => c.class_id === selectedClassId);
      toast.success(`Lesson started: ${cls?.name} — ${wholeDay ? 'Whole Day' : `Period ${selectedPeriod}`}`);
    } else {
      toast.error('Failed to start lesson');
    }
  };

  const handleEnd = async () => {
    setEnding(true);
    const result = await endSession();
    setEnding(false);
    if (result) {
      toast.success('Lesson ended. Data saved.');
      setSelectedClassId('');
      setSelectedPeriod('');
      setWholeDay(false);
    } else {
      toast.error('Failed to end lesson');
    }
  };

  const handleAppreciation = async () => {
    if (!session?.class_id || !session?.session_id) return;
    try {
      const [logsRes, studentsRes] = await Promise.all([
        fetch(`${API}/behaviour/${session.class_id}?session_id=${session.session_id}`, { credentials: 'include' }),
        fetch(`${API}/students/${session.class_id}`, { credentials: 'include' }),
      ]);
      const logs = logsRes.ok ? await logsRes.json() : [];
      const studs = studentsRes.ok ? await studentsRes.json() : [];
      setAppreciationData({ logs, students: studs });
      setActiveOverlay('appreciation');
    } catch (e) {
      toast.error('Failed to load behaviour data');
    }
  };

  const handleCustomBellAdd = () => {
    setCustomBells(prev => [...prev, { label: `Period ${prev.length + 1}`, time: '09:00' }]);
  };

  const handleCustomBellChange = (idx, field, value) => {
    setCustomBells(prev => prev.map((b, i) => i === idx ? { ...b, [field]: value } : b));
  };

  const handleCustomBellRemove = (idx) => {
    setCustomBells(prev => prev.filter((_, i) => i !== idx));
  };

  const isActive = session?.session_id && !session?.ended_at;

  return (
    <Layout breadcrumb={[{ label: 'Teach Now' }]}>
      <div className="space-y-5" data-testid="live-mode-page">

        {/* ===== ACTIVE LESSON DASHBOARD ===== */}
        {isActive && activeClass ? (
          <>
            {/* Lesson header bar */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft" data-testid="active-lesson-header">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 rounded-full bg-emerald-500 animate-pulse" />
                  <div>
                    <h1 className="text-lg font-heading font-semibold text-slate-900" data-testid="active-class-name">
                      {activeClass.name}
                      {activeClass.class_code && <span className="text-slate-400 font-normal ml-1">({activeClass.class_code})</span>}
                    </h1>
                    <p className="text-sm text-slate-500">
                      {session.period === 0 ? 'Whole Day' : `Period ${session.period}`} &middot; <ElapsedTimer startedAt={session.started_at} />
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleEnd}
                  disabled={ending}
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 rounded-xl h-10 px-5"
                  data-testid="end-lesson-btn"
                >
                  <Square className="w-4 h-4 mr-2 fill-current" />
                  {ending ? 'Ending...' : 'End Lesson'}
                </Button>
              </div>
            </div>

            {/* Live stats row */}
            {stats && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 animate-fade-in" data-testid="live-stats">
                <Card className="border-emerald-100 bg-emerald-50 rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <UserCheck className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                    <div>
                      <p className="text-xl font-heading font-bold text-emerald-700" data-testid="stat-present">
                        {stats.present + stats.late}/{stats.total_students}
                      </p>
                      <p className="text-[11px] text-emerald-600">Present</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-red-100 bg-red-50 rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <UserX className="w-5 h-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="text-xl font-heading font-bold text-red-600" data-testid="stat-absent">
                        {stats.absent}
                      </p>
                      <p className="text-[11px] text-red-500">Absent</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-amber-100 bg-amber-50 rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <ThumbsUp className="w-4 h-4 text-emerald-500" />
                      <ThumbsDown className="w-4 h-4 text-red-400" />
                    </div>
                    <div>
                      <p className="text-xl font-heading font-bold text-amber-700" data-testid="stat-behaviour">
                        {stats.behaviour_total}
                      </p>
                      <p className="text-[11px] text-amber-600">
                        {stats.behaviour_positive}+ / {stats.behaviour_negative}-
                      </p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-rose-100 bg-rose-50 rounded-xl">
                  <CardContent className="p-4 flex items-center gap-3">
                    <DoorOpen className="w-5 h-5 text-rose-500 flex-shrink-0" />
                    <div>
                      <p className="text-xl font-heading font-bold text-rose-600" data-testid="stat-ooc">
                        {stats.out_of_class}
                      </p>
                      <p className="text-[11px] text-rose-500">Out of Class</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Teacher Management section */}
            <div data-testid="teacher-management-section" className="mt-2">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-blue-500 mb-4 px-1" data-testid="management-heading">
                Teacher Management
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {MANAGEMENT_TOOLS.map((tool, i) => (
                  <ToolCard key={tool.path} tool={tool} classId={session.class_id} index={i} accent={MGMT_ACCENT} />
                ))}
              </div>
            </div>

            {/* Teacher Tools section */}
            <div data-testid="teacher-tools-section" className="mt-8">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-amber-500 mb-4 px-1" data-testid="tools-heading">
                Teacher Tools
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {TEACHER_TOOLS.map((tool, i) => (
                  <ToolCard key={tool.path} tool={tool} classId={session.class_id} index={i + MANAGEMENT_TOOLS.length} accent={TOOL_ACCENT} onOverlayClick={(path) => {
                    if (path === 'appreciation') { handleAppreciation(); }
                    else { setActiveOverlay(path); }
                  }} />
                ))}
              </div>
            </div>
          </>
        ) : (

          /* ===== IDLE — START A LESSON ===== */
          <>
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-soft" data-testid="session-selector">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
                <h1 className="text-lg font-heading font-semibold text-slate-900">Start a Lesson</h1>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {/* Class selector */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Class</label>
                  <Select
                    value={selectedClassId}
                    onValueChange={(val) => {
                      if (val === '__create_class__') {
                        setCreateClassOpen(true);
                        return;
                      }
                      setSelectedClassId(val);
                    }}
                  >
                    <SelectTrigger className="w-full rounded-xl border-slate-200 bg-slate-50 text-sm h-11" data-testid="class-select">
                      <SelectValue placeholder="Select a class..." />
                    </SelectTrigger>
                    <SelectContent>
                      {classes.map(c => (
                        <SelectItem key={c.class_id} value={c.class_id} data-testid={`class-option-${c.class_id}`}>
                          {c.name}{c.class_code ? ` (${c.class_code})` : ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="__create_class__" data-testid="create-class-option" className="text-teal-600 font-medium">
                        <span className="flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> Create New Class</span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Period selector */}
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">Period</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {Array.from({ length: periodsCount }, (_, i) => i + 1).map(p => (
                      <button
                        key={p}
                        onClick={() => { setSelectedPeriod(String(p)); setWholeDay(false); }}
                        className={`w-10 h-10 rounded-xl text-sm font-semibold transition-all ${
                          String(p) === selectedPeriod && !wholeDay
                            ? 'bg-teal-600 text-white shadow-sm scale-105'
                            : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                        }`}
                        data-testid={`period-btn-${p}`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => { setWholeDay(true); setSelectedPeriod(''); }}
                      className={`h-10 px-3 rounded-xl text-sm font-semibold transition-all flex items-center gap-1.5 ${
                        wholeDay
                          ? 'bg-amber-500 text-white shadow-sm scale-105'
                          : 'bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid="whole-day-btn"
                    >
                      <Sun className="w-3.5 h-3.5" />Whole Day
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                <Button
                  onClick={handleStart}
                  disabled={!selectedClassId || (!selectedPeriod && !wholeDay) || starting}
                  className="bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-11 px-6"
                  data-testid="start-lesson-btn"
                >
                  <Play className="w-4 h-4 mr-2 fill-current" />
                  {starting ? 'Starting...' : wholeDay ? 'Start Whole Day' : 'Start Lesson'}
                </Button>

                <button
                  onClick={() => setSpecialBellOpen(true)}
                  className={`h-11 px-4 rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                    specialDay
                      ? 'bg-violet-100 text-violet-700 border-2 border-violet-300'
                      : 'bg-slate-50 border border-slate-200 text-slate-500 hover:bg-slate-100'
                  }`}
                  data-testid="special-day-btn"
                >
                  <CalendarClock className="w-4 h-4" />
                  {specialDay ? 'Special Day Active' : 'Special Day'}
                </button>
              </div>
            </div>

            <div className="text-center py-12" data-testid="no-session">
              <Radio className="w-12 h-12 text-slate-200 mx-auto mb-3" />
              <h3 className="text-base font-heading font-semibold text-slate-700 mb-1">
                No active lesson
              </h3>
              <p className="text-sm text-slate-400">
                Select a class and period, then start your lesson
              </p>
            </div>
          </>
        )}
      </div>
      {activeOverlay === 'attention' && <AttentionGetterOverlay onClose={() => setActiveOverlay(null)} />}
      {activeOverlay === 'appreciation' && (
        <StudentAppreciationOverlay
          behaviourLogs={appreciationData.logs}
          students={appreciationData.students}
          onClose={() => setActiveOverlay(null)}
        />
      )}

      {/* Create Class Dialog */}
      <Dialog open={createClassOpen} onOpenChange={setCreateClassOpen}>
        <DialogContent className="bg-white max-w-sm rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-slate-900">Create New Class</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Class Name *</label>
              <Input
                autoFocus
                placeholder="e.g. Year 5B"
                value={newClass.name}
                onChange={(e) => setNewClass({ ...newClass, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateClass(); }}
                className="border-slate-200 rounded-xl"
                data-testid="new-class-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1.5 block">Class Code (optional)</label>
              <Input
                placeholder="e.g. 5B-MWF"
                value={newClass.class_code}
                onChange={(e) => setNewClass({ ...newClass, class_code: e.target.value })}
                className="border-slate-200 rounded-xl"
                data-testid="new-class-code-input"
              />
            </div>
            <Button
              onClick={handleCreateClass}
              disabled={!newClass.name.trim()}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl h-11"
              data-testid="create-class-submit-btn"
            >
              <Plus className="w-4 h-4 mr-2" />Create Class
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Special Day Bell Times Dialog */}
      <Dialog open={specialBellOpen} onOpenChange={setSpecialBellOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900 flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-violet-600" />Special Day Bell Times
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-500">
              Set custom bell times for today only. This won't change your saved timetable.
            </p>
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {customBells.map((bell, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={bell.label}
                    onChange={(e) => handleCustomBellChange(idx, 'label', e.target.value)}
                    className="flex-1 border-slate-200 text-sm"
                    placeholder="Period label"
                    data-testid={`custom-bell-label-${idx}`}
                  />
                  <Input
                    type="time"
                    value={bell.time}
                    onChange={(e) => handleCustomBellChange(idx, 'time', e.target.value)}
                    className="w-32 border-slate-200 text-sm"
                    data-testid={`custom-bell-time-${idx}`}
                  />
                  <button
                    onClick={() => handleCustomBellRemove(idx)}
                    className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 flex items-center justify-center transition-colors text-lg"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={handleCustomBellAdd} className="w-full border-dashed border-slate-300 text-slate-500" data-testid="add-bell-btn">
              + Add Bell Time
            </Button>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  if (customBells.length > 0) {
                    setSpecialDay(true);
                    toast.success('Special day bell times set for today');
                  } else {
                    setSpecialDay(false);
                  }
                  setSpecialBellOpen(false);
                }}
                className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl"
                data-testid="apply-special-day-btn"
              >
                {customBells.length > 0 ? 'Apply' : 'Close'}
              </Button>
              {specialDay && (
                <Button
                  variant="outline"
                  onClick={() => { setSpecialDay(false); setCustomBells([]); setSpecialBellOpen(false); toast.success('Reverted to normal bell times'); }}
                  className="border-red-200 text-red-600 hover:bg-red-50 rounded-xl"
                  data-testid="revert-bells-btn"
                >
                  Revert to Normal
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
